import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useNav } from '../context/NavContext';

const navIcons = {
  recipes: (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
  ),
  calendar: (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  ),
  grocery: (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
  ),
  nutrition: (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  ),
  cooking: (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  ),
  add: (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  ),
  profile: (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  ),
  admin: (
    <>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </>
  ),
  logout: (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
  )
};

function NavIcon({ name }) {
  return (
    <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      {navIcons[name]}
    </svg>
  );
}

function Navigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();
  const { isCollapsed, toggleCollapsed, isMobileMenuOpen, setIsMobileMenuOpen } = useNav();

  const navItems = [
    { path: '/recipes', label: 'All Recipes', icon: 'recipes' },
    { path: '/calendar', label: 'Calendar', icon: 'calendar' },
    { path: '/grocery-list', label: 'Grocery List', icon: 'grocery' },
    { path: '/nutrition', label: 'Nutrition', icon: 'nutrition' },
    { path: '/cooking-timeline', label: 'Cooking Timeline', icon: 'cooking' },
    { path: '/recipes/new', label: 'Add Recipe', icon: 'add' },
    { path: '/profile', label: 'Profile', icon: 'profile' }
  ];

  if (user?.isAdmin) {
    navItems.push({ path: '/admin', label: 'Admin', icon: 'admin' });
  }

  const isActive = (path) => {
    if (path === '/recipes') {
      return location.pathname === '/recipes';
    }
    if (path === '/recipes/new') {
      return location.pathname === '/recipes/new';
    }
    return location.pathname.startsWith(path);
  };

  const handleNavClick = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <button
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        className="lg:hidden fixed top-4 left-4 z-30 p-2 bg-white/90 backdrop-blur-sm rounded-lg shadow-lg"
        aria-label="Toggle menu"
      >
        <svg className="w-6 h-6 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          {isMobileMenuOpen ? (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          ) : (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          )}
        </svg>
      </button>

      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-20"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      <nav
        className={`fixed top-0 left-0 h-full bg-white/90 backdrop-blur-sm shadow-lg z-20 flex flex-col transition-all duration-300 ${
          isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 w-56 ${isCollapsed ? 'lg:w-16' : 'lg:w-56'}`}
      >
        <div className={`p-4 border-b border-gray-200 ${isCollapsed ? 'lg:px-2 lg:py-3' : ''}`}>
          <div className={`flex items-center ${isCollapsed ? 'lg:justify-center' : 'justify-between gap-2'}`}>
            <div className={isCollapsed ? 'lg:hidden min-w-0' : 'min-w-0'}>
              <h2 className="text-xl font-bold text-gray-800 truncate">Mealplan by Me</h2>
              {user && (
                <p className="text-sm text-gray-600 mt-1 truncate">{user.username}</p>
              )}
            </div>
            {isCollapsed && (
              <span
                className="hidden lg:inline-flex w-8 h-8 items-center justify-center rounded-lg bg-amber-600 text-white font-bold text-sm"
                title="Mealplan by Me"
              >
                M
              </span>
            )}
            <button
              type="button"
              onClick={toggleCollapsed}
              className="hidden lg:flex shrink-0 p-1.5 rounded-lg text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors"
              aria-label={isCollapsed ? 'Expand menu' : 'Collapse menu'}
              title={isCollapsed ? 'Expand menu' : 'Collapse menu'}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isCollapsed ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                )}
              </svg>
            </button>
          </div>
        </div>

        <div className={`flex-1 overflow-y-auto p-4 ${isCollapsed ? 'lg:px-2' : ''}`}>
          <ul className="space-y-2">
            {navItems.map((item) => (
              <li key={item.path}>
                <button
                  onClick={() => handleNavClick(item.path)}
                  title={isCollapsed ? item.label : undefined}
                  className={`w-full flex items-center gap-3 rounded-lg font-semibold transition-colors ${
                    isCollapsed ? 'lg:justify-center lg:px-2 lg:py-3 px-4 py-3' : 'px-4 py-3'
                  } ${
                    isActive(item.path)
                      ? 'bg-amber-600 text-white shadow-md'
                      : 'text-gray-700 hover:bg-amber-50 hover:text-amber-700'
                  }`}
                >
                  <NavIcon name={item.icon} />
                  <span className={isCollapsed ? 'lg:hidden' : ''}>{item.label}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className={`p-4 border-t border-gray-200 ${isCollapsed ? 'lg:px-2' : ''}`}>
          <button
            onClick={handleLogout}
            title={isCollapsed ? 'Logout' : undefined}
            className={`w-full flex items-center gap-3 rounded-lg font-semibold text-red-600 hover:bg-red-50 transition-colors ${
              isCollapsed ? 'lg:justify-center lg:px-2 lg:py-3 px-4 py-3' : 'px-4 py-3'
            }`}
          >
            <NavIcon name="logout" />
            <span className={isCollapsed ? 'lg:hidden' : ''}>Logout</span>
          </button>
        </div>
      </nav>
    </>
  );
}

export default Navigation;
